import { memo } from 'react';
import { Phone, MessageSquare, Trash2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { ILeadTableRowProps } from '@/@types/leads';
import { LeadAvatar } from './LeadAvatar';
import { LeadTypeBadge } from './LeadTypeBadge';
import { TemperatureBadge } from './TemperatureBadge';
import { PipelineStageBadge } from './PipelineStageBadge';
import { MessengerIcons } from './MessengerIcons';
import { SourceBadge } from './SourceBadge';

// Format relative time
function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}

export const LeadTableRow = memo(function LeadTableRow({
  lead,
  isSelected,
  onSelect,
  onClick,
  onCall,
  onMessage,
  onDelete,
  onRetrigger,
  showRequestsColumn = false,
  requestsCell,
}: ILeadTableRowProps) {
  return (
    <div
      className={cn(
        'flex items-center h-14 px-6 border-b border-table-row-border',
        'hover:bg-muted/30 transition-colors group cursor-pointer',
        isSelected && 'bg-primary/5'
      )}
      onClick={onClick}
    >
      {/* Checkbox column */}
      <div className="flex items-center justify-center w-10">
        <Checkbox
          checked={isSelected}
          onCheckedChange={checked => onSelect(checked === true)}
          onClick={e => e.stopPropagation()}
        />
      </div>

      {/* Name column */}
      <div className="flex-1 min-w-0 flex items-center gap-3">
        <LeadAvatar name={lead.name} profilePicture={lead.profilePicture} size="md" />
        <div className="min-w-0">
          <div className="font-medium text-foreground truncate">{lead.name}</div>
          <MessengerIcons
            hasWhatsApp={lead.hasWhatsApp}
            hasTelegram={lead.hasTelegram}
            email={lead.email}
            whatsappJid={lead.whatsappJid}
            telegramUserId={lead.telegramUserId}
            leadName={lead.name}
            leadPhone={lead.phoneNumber}
            checkStartedAt={lead.messengerCheckStartedAt}
            onRetrigger={onRetrigger ? () => onRetrigger(lead._id) : undefined}
          />
        </div>
      </div>

      {/* Source column */}
      <div className="w-[110px] pr-4">
        <SourceBadge source={lead.source} />
      </div>

      {/* Type column */}
      <div className="w-[100px] pr-4">
        {lead.leadType && <LeadTypeBadge leadType={lead.leadType} size="sm" />}
      </div>

      {/* Temp column */}
      <div className="w-[88px] pr-4">
        {lead.temperature && <TemperatureBadge temperature={lead.temperature} size="sm" />}
      </div>

      {/* Stage column */}
      <div className="w-[140px] pr-4">
        <PipelineStageBadge status={lead.status} size="sm" />
      </div>

      {/* Time column */}
      <div className="w-[100px]">
        <span className="text-sm text-muted-foreground">
          {formatRelativeTime(lead.lastMessageAt)}
        </span>
      </div>

      {/* Requests column — optional, hidden by default */}
      {showRequestsColumn && (
        <div className="w-[88px] pr-4 text-sm text-muted-foreground">{requestsCell}</div>
      )}

      {/* Actions column */}
      <div className="w-20 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {lead.phoneNumber && onCall && (
          <button
            className="p-1.5 rounded hover:bg-muted text-icon-btn-fg hover:text-foreground transition-colors"
            onClick={e => {
              e.stopPropagation();
              onCall();
            }}
          >
            <Phone className="w-[18px] h-[18px]" />
          </button>
        )}
        {onMessage && (
          <button
            className="p-1.5 rounded hover:bg-muted text-icon-btn-fg hover:text-foreground transition-colors"
            onClick={e => {
              e.stopPropagation();
              onMessage();
            }}
          >
            <MessageSquare className="w-[18px] h-[18px]" />
          </button>
        )}
        {onDelete && (
          <button
            className="p-1.5 rounded hover:bg-muted text-icon-btn-fg hover:text-destructive transition-colors"
            onClick={e => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Trash2 className="w-[18px] h-[18px]" />
          </button>
        )}
      </div>
    </div>
  );
});
